import { Web3DViewer } from '@technology/web3d';
import {
  CapabilityRegistryContainer,
  NavigationCapabilityCoordinator,
} from '@trimble/web3d';
import { FrustumCapability } from '@trimble/web3d-capabilities';
import { Quaternion, Vector3 } from 'three';
import { createSignal } from 'solid-js';
import { getLogger } from '@trimble/logging';
import { arePointsEqual, areQuaternionsEqual } from '@trimble/utilities';
import { SyncResolverFn } from './sync-coordinators/sync-coordinator';

const [isLoadingViewSignal, setIsLoadingViewSignal] = createSignal(false);

export class PaneSyncer {
  public static readonly isLoadingView = isLoadingViewSignal;
  public static readonly setIsLoadingView = setIsLoadingViewSignal;

  private cameraPosition: Vector3 | null = null;
  private cameraQuaternion: Quaternion | null = null;
  private cameraFov: number | null = null;
  private frustumPosition: Vector3 | null = null;
  private initialized: boolean = false;
  public paneIndexDebug: number = -1;
  private readonly logger = getLogger();

  constructor(
    private readonly viewer: Web3DViewer,
    private readonly registry: CapabilityRegistryContainer,
    private readonly syncResolver: SyncResolverFn,
    navCapCoord: NavigationCapabilityCoordinator | undefined
  ) {
    if (navCapCoord) {
      navCapCoord.resync = this.reset.bind(this);
    }
  }

  public getRegistry(): CapabilityRegistryContainer {
    return this.registry;
  }

  public reset(): void {
    this.logger.debug('PaneSyncer: reset', this.paneIndexDebug);
    this.initialized = false;
    this.cameraPosition = null;
    this.cameraQuaternion = null;
    this.cameraFov = null;
    this.frustumPosition = null;
  }

  public initialize(paneIndexDebug?: number): void {
    if (paneIndexDebug !== undefined) this.paneIndexDebug = paneIndexDebug;
    this.syncCameraCache();
    this.initialized = true;
  }

  public hasChanged(paneIndexDebug?: number): boolean {
    if (paneIndexDebug !== undefined) this.paneIndexDebug = paneIndexDebug;
    if (!this.initialized) {
      this.initialize(paneIndexDebug);
      return false;
    }

    // Do not treat camera moves driven by a view load as user interaction.
    if (PaneSyncer.isLoadingView()) {
      return false;
    }

    const isPositionUnchanged =
      this.cameraPosition &&
      arePointsEqual(this.viewer.camera.position, this.cameraPosition);
    const isQuaternionUnchanged =
      this.cameraQuaternion &&
      areQuaternionsEqual(
        this.viewer.camera.quaternion.toArray(),
        this.cameraQuaternion.toArray()
      );
    const isFovUnchanged = this.cameraFov === this.viewer.camera.fieldOfView;

    // Frustum position can drift independently from the three.js camera in
    // some view modes (map-overlay frustum, etc.). Include it so the sync
    // loop notices moves that don't touch viewer.camera.
    const currentFrustumPosition =
      this.registry.get(FrustumCapability)()?.position ?? null;
    const isFrustumPositionUnchanged =
      this.frustumPosition === null && currentFrustumPosition === null
        ? true
        : this.frustumPosition !== null &&
          currentFrustumPosition !== null &&
          arePointsEqual(currentFrustumPosition, this.frustumPosition);

    return !(
      isPositionUnchanged &&
      isQuaternionUnchanged &&
      isFovUnchanged &&
      isFrustumPositionUnchanged
    );
  }

  public async triggerSync(
    sourcePane: CapabilityRegistryContainer,
    paneIndexDebug?: number
  ): Promise<void> {
    if (PaneSyncer.isLoadingView()) return;

    if (paneIndexDebug !== undefined) this.paneIndexDebug = paneIndexDebug;
    this.logger.debug(
      `PaneSyncer ${paneIndexDebug?.toString() || 'unknown'}: triggering sync from source`
    );
    const coordinator = this.syncResolver(sourcePane, this.registry);
    await coordinator.sync(sourcePane, this.registry);
    this.syncCameraCache();
    this.initialized = true;
    this.logger.debug(
      `PaneSyncer ${paneIndexDebug?.toString() || 'unknown'}: sync complete`
    );
  }

  public syncCameraCache(): void {
    this.cameraPosition = this.viewer.camera.position.clone();
    this.cameraQuaternion = this.viewer.camera.quaternion.clone();
    this.cameraFov = this.viewer.camera.fieldOfView;
    this.frustumPosition =
      this.registry.get(FrustumCapability)()?.position?.clone() ?? null;
  }
}
